import React, { useState } from "react";
import CTASection from "../../components/cta-section";
import { useTranslation } from "../../i18n";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Zap,
  MapPin,
  BarChart,
  ArrowRight,
  Building2,
  Users,
  Play,
  Calendar,
  Target,
  Rocket,
  Shield,
  Globe,
  Star,
  TrendingUp,
  Database,
  Smartphone,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function SmartCityRoute() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 font-medium text-sm mb-6">
                <Star className="w-4 h-4 mr-2" />
                {t("routes.smartCity.featuredBadge")}
              </div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {t("routes.smartCity.heroTitle")}{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
                  {t("routes.smartCity.heroHighlight")}
                </span>
              </h1>

              <p className="text-xl text-gray-600 mb-8 max-w-2xl">
                {t("routes.smartCity.heroDescription")}
              </p>
            </div>

            <div className="lg:w-1/2 flex justify-center">
              <div className="relative">
                {/* Main Dashboard Mockup */}
                <div className="relative bg-gradient-to-br from-white to-slate-50 rounded-3xl p-8 shadow-2xl border border-slate-200/50 backdrop-blur-sm max-w-lg">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                    <div className="text-sm font-medium text-slate-600">
                      {t("routes.smartCity.dashboardTitle")}
                    </div>
                  </div>

                  {/* Service Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4 border border-purple-200/50">
                      <Building2 className="w-8 h-8 text-purple-600 mb-3" />
                      <h3 className="font-semibold text-slate-800 text-sm">
                        {t("routes.smartCity.serviceBikeParking")}
                      </h3>
                      <p className="text-xs text-slate-600 mt-1">
                        {t("routes.smartCity.serviceBikeParkingCount")}
                      </p>
                      <div className="mt-2 bg-purple-200 rounded-full h-1">
                        <div className="bg-purple-600 h-1 rounded-full w-3/4"></div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4 border border-blue-200/50">
                      <Zap className="w-8 h-8 text-blue-600 mb-3" />
                      <h3 className="font-semibold text-slate-800 text-sm">
                        {t("routes.smartCity.serviceEvCharging")}
                      </h3>
                      <p className="text-xs text-slate-600 mt-1">
                        {t("routes.smartCity.serviceEvChargingCount")}
                      </p>
                      <div className="mt-2 bg-blue-200 rounded-full h-1">
                        <div className="bg-blue-600 h-1 rounded-full w-2/3"></div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-4 border border-emerald-200/50">
                      <MapPin className="w-8 h-8 text-emerald-600 mb-3" />
                      <h3 className="font-semibold text-slate-800 text-sm">
                        {t("routes.smartCity.serviceSmartParking")}
                      </h3>
                      <p className="text-xs text-slate-600 mt-1">
                        {t("routes.smartCity.serviceSmartParkingCount")}
                      </p>
                      <div className="mt-2 bg-emerald-200 rounded-full h-1">
                        <div className="bg-emerald-600 h-1 rounded-full w-4/5"></div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-4 border border-orange-200/50">
                      <Users className="w-8 h-8 text-orange-600 mb-3" />
                      <h3 className="font-semibold text-slate-800 text-sm">
                        {t("routes.smartCity.serviceCitizenApp")}
                      </h3>
                      <p className="text-xs text-slate-600 mt-1">
                        {t("routes.smartCity.serviceCitizenAppCount")}
                      </p>
                      <div className="mt-2 bg-orange-200 rounded-full h-1">
                        <div className="bg-orange-600 h-1 rounded-full w-5/6"></div>
                      </div>
                    </div>
                  </div>

                  {/* Unified Platform Badge */}
                  <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-4 text-center text-white">
                    <p className="font-semibold text-sm">
                      {t("routes.smartCity.unifiedPlatformTitle")}
                    </p>
                    <p className="text-xs opacity-90 mt-1">
                      {t("routes.smartCity.unifiedPlatformSubtitle")}
                    </p>
                  </div>
                </div>

                {/* Floating Cards */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl border border-slate-200/50 backdrop-blur-sm">
                  <TrendingUp className="w-6 h-6 text-green-600 mb-2" />
                  <div className="text-sm font-semibold text-slate-800">
                    +23%
                  </div>
                  <div className="text-xs text-slate-600">
                    {t("routes.smartCity.usageGrowth")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-100 text-purple-700 font-medium text-sm mb-6">
              <Database className="w-4 h-4 mr-2" />
              {t("routes.smartCity.badgeEnterprise")}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              {t("routes.smartCity.heroTitle")}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                {" "}
                {t("routes.smartCity.heroHighlight")}
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t("routes.smartCity.overviewDescription")}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-700 rounded-3xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-slate-200/50 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-slate-900">
                  {t("routes.smartCity.cityAdminTitle")}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t("routes.smartCity.cityAdminDesc")}
                </p>
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-purple-300 rounded-full"></div>
                  </div>
                  <span className="text-sm text-slate-500 font-medium">
                    {t("routes.smartCity.cityAdminBullet")}
                  </span>
                </div>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-slate-200/50 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-slate-900">
                  {t("routes.smartCity.innovationTeamsTitle")}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t("routes.smartCity.innovationTeamsDesc")}
                </p>
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-blue-300 rounded-full"></div>
                  </div>
                  <span className="text-sm text-slate-500 font-medium">
                    {t("routes.smartCity.innovationTeamsBullet")}
                  </span>
                </div>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-3xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-slate-200/50 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-slate-900">
                  {t("routes.smartCity.citizensTitle")}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t("routes.smartCity.citizensDesc")}
                </p>
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-emerald-300 rounded-full"></div>
                  </div>
                  <span className="text-sm text-slate-500 font-medium">
                    {t("routes.smartCity.citizensBullet")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium text-sm mb-6">
              <Rocket className="w-4 h-4 mr-2" />
              {t("routes.smartCity.processBadge")}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              {t("routes.smartCity.processTitlePrefix")}{" "}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                {t("routes.smartCity.processTitleHighlight")}
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              {t("routes.smartCity.processDescription")}
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-purple-500 via-blue-500 to-emerald-500 rounded-full hidden lg:block"></div>

            <div className="space-y-20">
              {/* Step 1 */}
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-16 mb-8 lg:mb-0">
                  <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-500">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-2xl mr-6 shadow-lg">
                        1
                      </div>
                      <h3 className="text-2xl font-bold text-white">
                        {t("routes.smartCity.step1Title")}
                      </h3>
                    </div>
                    <p className="text-slate-300 mb-6 leading-relaxed text-lg">
                      {t("routes.smartCity.step1Desc")}
                    </p>
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-purple-400" />
                      <span className="text-purple-400 font-medium">
                        {t("routes.smartCity.step1Duration")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/2 lg:pl-16">
                  <div className="relative">
                    <div className="w-full h-80 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-purple-500/30">
                      <Target className="w-20 h-20 text-purple-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col lg:flex-row-reverse items-center">
                <div className="lg:w-1/2 lg:pl-16 mb-8 lg:mb-0">
                  <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-500">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white font-bold text-2xl mr-6 shadow-lg">
                        2
                      </div>
                      <h3 className="text-2xl font-bold text-white">
                        {t("routes.smartCity.step2Title")}
                      </h3>
                    </div>
                    <p className="text-slate-300 mb-6 leading-relaxed text-lg">
                      {t("routes.smartCity.step2Desc")}
                    </p>
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-400 font-medium">
                        {t("routes.smartCity.step2Duration")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/2 lg:pr-16">
                  <div className="relative">
                    <div className="w-full h-80 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-blue-500/30">
                      <Rocket className="w-20 h-20 text-blue-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-16 mb-8 lg:mb-0">
                  <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-500">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white font-bold text-2xl mr-6 shadow-lg">
                        3
                      </div>
                      <h3 className="text-2xl font-bold text-white">
                        {t("routes.smartCity.step3Title")}
                      </h3>
                    </div>
                    <p className="text-slate-300 mb-6 leading-relaxed text-lg">
                      {t("routes.smartCity.step3Desc")}
                    </p>
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-400 font-medium">
                        {t("routes.smartCity.step3Duration")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/2 lg:pl-16">
                  <div className="relative">
                    <div className="w-full h-80 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-emerald-500/30">
                      <Globe className="w-20 h-20 text-emerald-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Replace the Applications Section header content */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 font-medium text-sm mb-6">
              <CheckCircle className="w-4 h-4 mr-2" />
              {t("routes.smartCity.applicationsBadge")}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              {t("routes.smartCity.applicationsTitlePrefix")}{" "}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                {t("routes.smartCity.applicationsTitleHighlight")}
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t("routes.smartCity.applicationsDescription")}
            </p>
          </div>

          {/* Replace Feature 1 content */}
          <div className="flex flex-col lg:flex-row items-center mb-24 gap-16">
            <div className="lg:w-1/2">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/20250701_2125_European%20Control%20Center_remix_01jz3q4fh1ecrt3n7xzf2h0by0-0FQSDpcizlcgUhGImQT2q5fLEgeQOq.png"
                    alt={t("routes.smartCity.feature1ImageAlt")}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <h3 className="text-3xl font-bold mb-8 text-slate-900">
                {t("routes.smartCity.feature1Title")}
              </h3>
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200/50 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <Zap className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2 text-lg">
                        {t("routes.smartCity.centralizedPlatform")}
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {t("routes.smartCity.centralizedPlatformDesc")}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200/50 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <BarChart className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2 text-lg">
                        {t("routes.smartCity.realTimeMonitoring")}
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {t("routes.smartCity.realTimeMonitoringDesc")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Replace Feature 2 content */}
          <div className="flex flex-col lg:flex-row-reverse items-center mb-24 gap-16">
            <div className="lg:w-1/2">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-3xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/20250701_2134_Group%20Examines%20Smartphone_remix_01jz3qn89jffwb39h8v772b49t-Ng1snuoTjEFHKJhGfdfnn0sGCxF5pK.png"
                    alt={t("routes.smartCity.feature2ImageAlt")}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <h3 className="text-3xl font-bold mb-8 text-slate-900">
                {t("routes.smartCity.feature2Title")}
              </h3>
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200/50 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <Smartphone className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2 text-lg">
                        {t("routes.smartCity.unifiedAppAccess")}
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {t("routes.smartCity.unifiedAppAccessDesc")}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200/50 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2 text-lg">
                        {t("routes.smartCity.personalizedServices")}
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {t("routes.smartCity.personalizedServicesDesc")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Replace Features 3 & 4 grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-700 rounded-3xl blur opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-white to-purple-50 rounded-3xl p-8 shadow-xl border border-purple-100">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg">
                  <Clock className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-6 text-slate-900">
                  {t("routes.smartCity.feature3Title")}
                </h3>
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full bg-white border border-purple-200 flex items-center justify-center shadow-sm">
                        <CheckCircle className="w-4 h-4 text-purple-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="font-bold text-slate-900 mb-1">
                        {t("routes.smartCity.deploymentShort")}
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {t("routes.smartCity.deploymentDesc")}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full bg-white border border-purple-200 flex items-center justify-center shadow-sm">
                        <CheckCircle className="w-4 h-4 text-purple-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="font-bold text-slate-900 mb-1">
                        {t("routes.smartCity.prebuiltIntegrations")}
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {t("routes.smartCity.prebuiltIntegrationsDesc")}
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl blur opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-white to-blue-50 rounded-3xl p-8 shadow-xl border border-blue-100">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg">
                  <Shield className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-6 text-slate-900">
                  {t("routes.smartCity.feature4Title")}
                </h3>
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full bg-white border border-blue-200 flex items-center justify-center shadow-sm">
                        <CheckCircle className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="font-bold text-slate-900 mb-1">
                        {t("routes.smartCity.openApis")}
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {t("routes.smartCity.openApisDesc")}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full bg-white border border-blue-200 flex items-center justify-center shadow-sm">
                        <CheckCircle className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="font-bold text-slate-900 mb-1">
                        {t("routes.smartCity.scalableInfrastructure")}
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {t("routes.smartCity.scalableInfrastructureDesc")}
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      {/* <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 font-medium text-sm mb-6">
              <Star className="w-4 h-4 mr-2" />
              {t('routes.smartCity.caseStudiesBadge')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              {t('routes.smartCity.trustedBy')} {" "}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                {t('routes.smartCity.trustedByHighlight')}
              </span>
            </h2>
            <p className="text-xl text-slate-600">
              {t('routes.smartCity.trustedByDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-700 rounded-3xl blur opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-slate-200/50 hover:shadow-2xl transition-all duration-500">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mr-4">
                    <Building2 className="w-8 h-8 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      {t('routes.smartCity.case1Title')}
                    </h3>
                    <p className="text-purple-600 font-semibold">
                      {t('routes.smartCity.case1Subtitle')}
                    </p>
                  </div>
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed text-lg">
                  {t('routes.smartCity.case1Desc')}
                </p>
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
                  <p className="text-slate-700 italic leading-relaxed">
                    {t('routes.smartCity.case1Quote')}
                  </p>
                  <p className="text-sm text-purple-600 mt-3 font-medium">
                    {t('routes.smartCity.case1Author')}
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl blur opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-slate-200/50 hover:shadow-2xl transition-all duration-500">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mr-4">
                    <Zap className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      {t('routes.smartCity.case2Title')}
                    </h3>
                    <p className="text-blue-600 font-semibold">
                      {t('routes.smartCity.case2Subtitle')}
                    </p>
                  </div>
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed text-lg">
                  {t('routes.smartCity.case2Desc')}
                </p>
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                  <p className="text-slate-700 italic leading-relaxed">
                    {t('routes.smartCity.case2Quote')}
                  </p>
                  <p className="text-sm text-blue-600 mt-3 font-medium">
                    {t('routes.smartCity.case2Author')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <CTASection />
    </div>
  );
}
